import {
	Alert,
	AlertIcon,
	Avatar,
	Box,
	Button,
	Divider,
	HStack,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	StackDivider,
	Tag,
	TagLabel,
	TagLeftIcon,
	Text,
	Textarea,
	VStack,
	Wrap,
	WrapItem,
	useDisclosure,
	useToast,
} from "@chakra-ui/react";
import * as _ from "lodash";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import { Calendar } from "../components/Calendar";
import LoadingIndicator from "../components/Loading";
import { ShiftRow } from "../components/ShiftRow";
import { Back } from "../components/button/Back";
import { CompetencePills } from "../components/competence-pill/CompetencePill";
import { LoggedInLayout } from "../components/layouts/LoggedInLayout";
import { EditLink } from "../components/text/EditLink";
import moment from "moment";
import styled from "styled-components";
import FileThumbnail from "../components/ChakraUI/FileThumbnail";
import SimpleSelect from "../components/ChakraUI/SimpleSelect";
import TagList, { TagOption } from "../components/ChakraUI/TagList";
import Lock from "../components/icons/Lock";
import ConsultantAvatar from "../components/jobView/ConsultantAvatar";
import { PageTitle } from "../components/text/PageTitle";
import { JobApplication } from "@models/JobApplication";
import { FileData, ShiftItem } from "@models/Order";
import {
	useCancelJob,
	useCustomerCompetences,
	useJob,
	useJobApplicationUpdate,
	useJobApplications,
	useOrganisation,
	useUpdateJob,
	useUpdateShifts,
} from "@api/hooks";
import { routes } from "../routes";
import { appTheme } from "@styles/Theme";
import { getApplicantConsultantData, getFilteredConsultantData } from "@utils/consultantUtils";
import { getFormatTime, getTimeDiffInHours, isValidTime } from "@utils/dateUtils";
import {
	findLastShift,
	formatDateTime,
	getJobStatusTagDetails,
	getServiceName,
	getStatusDescription,
} from "@utils/utils";
import { getPublicConfig } from "../api/config";

const showMaxShifts = 8;

const JobDetailPage = () => {
	const navigate = useNavigate();
	const toast = useToast();
	const params = useParams();
	const [searchParams] = useSearchParams();
	const jobId = params.id as string;
	let consultantFeedback: any = searchParams.get("consultantFeedback") ?? "false";
	if (consultantFeedback === "true") {
		consultantFeedback = true;
	} else {
		consultantFeedback = false;
	}

	const { isOpen, onOpen, onClose } = useDisclosure();
	const {
		isOpen: isOpenApplicationAcceptModal,
		onOpen: onOpenApplicationAcceptModal,
		onClose: onCloseApplicationAcceptModal,
	} = useDisclosure();
	const { isOpen: isOpenCancelModal, onOpen: onOpenCancelModal, onClose: onCloseCancelModal } = useDisclosure();

	// API hooks
	const { organisation } = useOrganisation();
	const { mutate: updateJobMutation, isLoading: isLoadingJobUpdate } = useUpdateJob(
		jobId,
		() => {
			toast({
				title: "Uppdateringen av uppdraget lyckades",
				status: "success",
				duration: 5000,
				isClosable: true,
				variant: "subtle",
			});
		},
		(error: string) => {
			toast({
				title: "Uppdateringen av uppgiften misslyckades",
				description: error,
				status: "error",
				duration: 9000,
				isClosable: true,
				variant: "subtle",
			});
		}
	);
	const { mutate: cancelJobMutation, isLoading: isLoadingJobCancel } = useCancelJob(
		jobId,
		() => {
			toast({
				title: "Uppdraget återkallades",
				status: "success",
				duration: 9000,
				isClosable: true,
				variant: "subtle",
			});
			isOpenCancelModal && onCloseCancelModal();
			navigate(-1);
		},
		() => {
			toast({
				title: "Uppgiften kunde inte avbrytas",
				status: "error",
				duration: 9000,
				isClosable: true,
				variant: "subtle",
			});
		}
	);
	const { mutate: applicationUpdateMutation, isLoading: isLoadingJobApplicationUpdate } = useJobApplicationUpdate(
		() => {
			toast({
				title: "Uppdraget uppdaterat",
				status: "success",
				duration: 9000,
				isClosable: true,
				variant: "subtle",
			});
			refetch();
			isOpenApplicationAcceptModal && onCloseApplicationAcceptModal();
		},
		() => {
			toast({
				title: "Uppdateringen av uppdraget misslyckades",
				status: "error",
				duration: 9000,
				isClosable: true,
				variant: "subtle",
			});
		}
	);
	const { job, isLoading, isError, refetch } = useJob(jobId);
	const { applications, invitations } = useJobApplications(jobId);
	const { mutate: updateShiftsMutation, isLoading: isLoadingDeleteShift } = useUpdateShifts(
		() => {
			toast({
				title: "Uppdatering av arbetsskift lyckades",
				status: "success",
				duration: 5000,
				isClosable: true,
				variant: "subtle",
			});
		},
		(error: string) => {
			toast({
				title: "Arbetsskift uppdateringsfel",
				description: error,
				status: "error",
				duration: 9000,
				isClosable: true,
				variant: "subtle",
			});
		}
	);
	const { competences } = useCustomerCompetences(job?.customer.id);

	// Job is editable as long as it is open
	const isEditable = job?.status === "OPEN";

	// Shifts and competences cannot be edited after we have received at least one
	// application otherwise the consultant might not longer be able to work that time if shifts
	// has been changed.
	const isShiftsAndCompetenceEditable = isEditable && !job.applications?.length;

	// Allow to extend job if job is booked and last shift was less than 7 days ago
	// TODO: Think about moving this to BE, like a flag "closed" or something
	const canExtendJob = job?.status === "BOOKED" && moment().diff(job.endDate, "days") < 7;

	// State
	const [showAllShifts, setShowAllShifts] = useState(false);
	const [jobDescription, setJobDescription] = useState("");
	const [jobCustomerDescription, setJobCustomerDescription] = useState("");
	const [selectedBranch, setSelectedBranch] = useState("");
	const [selectedService, setSelectedService] = useState("");
	const [shifts, setShifts] = useState<ShiftItem[]>([]);
	const [skills, setSkills] = useState<TagOption[]>([]);
	const [selectedConsultantApplicationId, setSelectedConsultantApplicationId] = useState("");
	const [files, setFiles] = useState<File[]>([]);
	const fileUploadInputRef = useRef<HTMLInputElement>(null);
	const [isLoadingFileUpload, setIsLoadingFileUpload] = useState<boolean>(false);
	const [fileErrorMessage, setFileErrorMessage] = useState<string>("");
	const [imageFiles, setImageFiles] = useState<FileData[]>([]);
	const [documentFiles, setDocumentFiles] = useState<FileData[]>([]);

	const FileUploadRules = [
		{
			validTypes: ["image/png", "image/jpeg"],
			maxQuantity: 2,
			warningMessage: "Maximalt antal bilder är 2",
			currentQuantity: imageFiles.length ?? 0,
		},
		{
			validTypes: ["application/pdf"],
			maxQuantity: 2,
			warningMessage: "Maximalt antal dokument är 2",
			currentQuantity: documentFiles.length ?? 0,
		},
	];

	useEffect(() => {
		job?.competence.branch && setSelectedBranch(job?.competence.branch);
		job?.competence.service && setSelectedService(job?.competence.service);
		job?.customerDescription && setJobCustomerDescription(job?.customerDescription);
		job?.description && setJobDescription(job?.description);
		job?.shifts && setShifts(job.shifts);
		job?.documents && setDocumentFiles(job.documents);
		job?.images && setImageFiles(job.images);
	}, [job]);

	const services = useMemo(() => {
		const selectedBranchData = competences?.find((c) => c.key === selectedBranch);
		if (selectedBranchData) {
			return selectedBranchData.items;
		}
		return [];
	}, [selectedBranch, competences]);

	useEffect(() => {
		const skillsData = services?.find((s) => s.key === selectedService)?.items ?? [];
		if (job?.competence?.skills && job?.competence?.skills?.length > 0) {
			setSkills(
				skillsData.map((skill) => ({
					key: skill.key,
					value: skill.name.sv as string,
					isSelected: job?.competence.skills?.includes(skill.key),
				}))
			);
		} else {
			setSkills(
				skillsData.map((skill) => ({
					key: skill.key,
					value: skill.name.sv as string,
					isSelected: false,
				}))
			);
		}
	}, [selectedService, job?.competence?.skills, services]);

	const selectedBranchServices = useMemo(() => {
		if (selectedBranch && competences) {
			return competences.find((b) => b.key === selectedBranch)?.items || [];
		}
		return [];
	}, [selectedBranch, competences]);

	const selectedServiceSkills = useMemo(() => {
		if (selectedBranchServices && selectedService && competences) {
			return selectedBranchServices.find((s) => s.key === selectedService)?.items || [];
		}
		return [];
	}, [selectedBranchServices, competences, selectedService]);

	const showSkills = useMemo(() => {
		if (selectedBranch && selectedService) {
			const selectedBranchData = competences?.find((c) => c.key === selectedBranch);
			if (selectedBranchData) {
				const selectedServiceData = selectedBranchData.items?.find((c) => c.key === selectedService);
				return !!selectedServiceData?.items?.length;
			}
		}
		return false;
	}, [selectedBranch, selectedService, competences]);

	useEffect(() => {
		if (files.length > 0) {
			uploadFileData(files);
		}
	}, [files]);

	const uploadFileData = async (files: File[]) => {
		setIsLoadingFileUpload(true);
		try {
			for (const file of files) {
				await handleUpload(file);
			}
		} catch (error) {
			setIsLoadingFileUpload(false);
			console.log({ error });
		}
	};

	const handleUpload = async (file: File) => {
		setIsLoadingFileUpload(true);
		const formData = new FormData();
		formData.append("file", file);

		if (organisation?.id) {
			await fetch(`${getPublicConfig("fileUploadRoot")}/upload?path=/${organisation.id}`, {
				method: "POST",
				body: formData,
			})
				.then((response) => {
					if (!response.ok) {
						console.error(response);
						throw new Error("Network response was not ok");
					}
					return response.json();
				})
				.then((data) => {
					const { amazonUrl, url, originalName, mimeType } = data.data;
					if (mimeType === "application/pdf") {
						setDocumentFiles((prev) => [...prev, { url: url ?? amazonUrl, name: originalName }]);
					} else {
						setImageFiles((prev) => [...prev, { url: url ?? amazonUrl, name: originalName }]);
					}
				})
				.catch((error) => {
					console.error("There was a problem with file upload:", error);
					toast({
						title: "Det uppstod ett problem med filuppladdningen",
						status: "error",
						duration: 9000,
						isClosable: true,
						variant: "subtle",
					});
				})
				.finally(() => {
					setFiles([]);
					setIsLoadingFileUpload(false);
				});
		}
	};

	const removeShift = async (shiftIdToRemove: string | undefined, shiftToRemoveStart: string) => {
		if (shiftIdToRemove) {
			const newShifts = shifts.filter((s) => s.id !== shiftIdToRemove);
			if (newShifts.length === 0) {
				return toast({
					title: "Fel vid radering av arbetsskift",
					description: "Uppdraget måste innehålla minst ett arbetspass.",
					status: "error",
					duration: 5000,
					isClosable: true,
					variant: "subtle",
				});
			}
			await updateShiftsMutation({ jobId, shifts: newShifts });
		} else {
			setShifts((prev) => prev.filter((si) => si.start !== shiftToRemoveStart));
		}
	};

	const updateShift = (updatedShift: ShiftItem) => {
		const shiftToUpdate = {
			...updatedShift,
			break: updatedShift.break
				? updatedShift.break
				: getTimeDiffInHours(moment(updatedShift.start).format("HH:mm"), moment(updatedShift.end).format("HH:mm")) >= 5
				? 0.5
				: 0,
		};
		if (shiftToUpdate.id) {
			setShifts((prev) => {
				const updatedShiftList = [...prev];
				const updatedShiftIndex = prev.findIndex((s) => s.id === shiftToUpdate.id);
				updatedShiftList.splice(updatedShiftIndex, 1, shiftToUpdate);
				return updatedShiftList;
			});
		} else {
			setShifts((prev) => {
				const updatedShiftList = [...prev];
				const updatedShiftIndex = prev.findIndex((s) => moment(s.start).isSame(shiftToUpdate.start, "day"));
				updatedShiftList.splice(updatedShiftIndex, 1, shiftToUpdate);
				return updatedShiftList;
			});
		}
	};

	const appendShift = () => {
		const lastShift = findLastShift(shifts);

		setShifts([
			...shifts,
			{
				start: moment(lastShift.start).add(1, "day").toISOString(),
				end: moment(lastShift.end).add(1, "day").toISOString(),
			},
		]);
	};

	const bookedConsultant = (job?.applications || []).find((a) => a.status === "ACCEPTED");
	const isBooked = ["BOOKED", "BOOKED_WITH_UNMANNED_SHIFTS"].includes(job?.status as string);
	const hasEnded = isBooked && moment().isAfter(job?.endDate);
	// @ts-ignore
	const acceptedConsultants = getApplicantConsultantData(["ACCEPTED"], job);
	const { invitedConsultants, appliedConsultants } = getFilteredConsultantData(
		["OPEN", "DECLINED", "EXPIRED"],
		["PENDING_ANSWER"],
		// @ts-ignore
		applications,
		invitations
	);

	const getConsultantForShift = (shiftId?: string): string => {
		if (shiftId && job?.applications)
			return (
				job?.applications.find((application) => application.shifts?.find((shift) => shift.id === shiftId))?.consultant
					.firstName ?? ""
			);
		return "";
	};

	const getAvatarForShift = (shiftId?: string): React.ReactNode => {
		if (shiftId && job?.applications) {
			const profilePicture = job?.applications.find((application) =>
				application.shifts?.find((shift) => shift.id === shiftId)
			)?.consultant.profile?.profilePicture;

			return profilePicture ? (
				<Avatar size="sm" src={profilePicture} />
			) : (
				<Tag size="lg" colorScheme="pink" color={"#BE123C"} borderRadius="full" border={"1px solid #BE123C"}>
					?
				</Tag>
			);
		}
		return (
			<Tag size="lg" colorScheme="pink" borderRadius="full" border={"1px solid #BE123C"} color={"#BE123C"}>
				?
			</Tag>
		);
	};

	const isConsultantAbsentForShift = (shift: ShiftItem): boolean => {
		if (!shift.id || !job?.consultantShifts) return false;
		return !!job.consultantShifts.find((cs) => cs.shiftId === shift.id)?.consultantAbsent;
	};

	const handleSkillSelect = (itemKey: string) => {
		setSkills((prev) => {
			const skillList = [...prev];
			const itemIndex = prev.findIndex((s) => s.key === itemKey);
			if (itemIndex >= 0) {
				const changedItem = { ...prev[itemIndex] };
				changedItem.isSelected = changedItem.isSelected ? false : true;
				skillList.splice(itemIndex, 1, changedItem);
			}
			return skillList;
		});
	};

	// job changes except shift changes
	const hasChangedJobData = (): boolean => {
		if (!job) return false;
		if (!_.isEqual(job?.customerDescription, jobCustomerDescription)) {
			return true;
		}
		if (!_.isEqual(job?.description, jobDescription)) {
			return true;
		}
		if (
			job.competence.branch !== selectedBranch ||
			job.competence.service !== selectedService ||
			!_.isEqual(_.sortBy(job.competence?.skills), _.sortBy(skills.filter((s) => s.isSelected).map((s) => s.key)))
		) {
			return true;
		}
		if (
			(job.documents &&
				(job.documents.length !== documentFiles.length ||
					!_.isEqual(_.sortBy(job.documents, "url"), _.sortBy(documentFiles, "url")))) ||
			(job.images &&
				(job.images.length !== imageFiles.length ||
					!_.isEqual(_.sortBy(job.images, "url"), _.sortBy(imageFiles, "url"))))
		) {
			return true;
		}
		return false;
	};

	const hasChangedShiftData = (): boolean => {
		if (!job) return false;
		if (!_.isEqual(shifts, job?.shifts)) {
			return true;
		}
		return false;
	};

	const hasChangedOverallData = (): boolean => {
		return hasChangedJobData() || hasChangedShiftData();
	};

	const handleBackNavigation = () => {
		if (hasChangedOverallData() && isEditable) {
			onOpen();
		} else {
			navigate(-1);
		}
	};

	const handleUpdateJob = async () => {
		const hasServiceSkills = !!selectedServiceSkills.length;
		hasChangedJobData() &&
			(await updateJobMutation({
				customerDescription: jobCustomerDescription,
				description: jobDescription,
				competence: {
					branch: selectedBranch,
					service: selectedService,
					skills: hasServiceSkills ? skills.filter((sk) => sk.isSelected).map((s) => s.key) : undefined,
				},
				documents: documentFiles ?? undefined,
				images: imageFiles ?? undefined,
			}));

		if (hasChangedShiftData()) {
			if (
				!!shifts.find(
					(s) =>
						(isValidTime(s.start) && !isValidTime(s.end)) ||
						(!isValidTime(s.start) && isValidTime(s.end)) ||
						(isValidTime(s.start) &&
							isValidTime(s.end) &&
							getTimeDiffInHours(getFormatTime(s.start), getFormatTime(s.end)) < 4)
				)
			)
				return;
			else await updateShiftsMutation({ jobId, shifts });
		}
	};

	const handleJobCancel = async () => {
		if (job && job.status === "OPEN") {
			try {
				await cancelJobMutation({});
			} catch (error) {
				console.error({ handleJobCancel: error });
				toast({
					title: "Något gick fel, försök igen senare",
					status: "error",
					duration: 5000,
					isClosable: true,
					variant: "subtle",
				});
			}
		}
		return;
	};

	const handleApplicationAccept = async (status: "DECLINED" | "ACCEPTED") => {
		if (job?.id) {
			try {
				await applicationUpdateMutation({
					jobId: job?.id,
					applicationId: selectedConsultantApplicationId,
					body: { status },
				});
			} catch (error) {
				console.error({ jobApplicationAccept: error });
				toast({
					title: "Något gick fel, försök igen senare",
					status: "error",
					duration: 5000,
					isClosable: true,
					variant: "subtle",
				});
			}
		}
	};

	const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files && event.target.files.length > 0) {
			const selectedFiles = Array.from(event.target.files);
			// check the validation
			const uploadedFileRules = FileUploadRules.map((u) => ({
				...u,
				toBeUploadedFileCount: selectedFiles.filter((f) => u.validTypes.includes(f.type)).length,
			}));
			const violatedFileRule = uploadedFileRules.find(
				(ufr) => ufr.toBeUploadedFileCount + ufr.currentQuantity > ufr.maxQuantity
			);
			if (!!violatedFileRule) {
				setFileErrorMessage(violatedFileRule.warningMessage);
				return;
			}
			setFiles((prev) => [...prev, ...selectedFiles]);
			setFileErrorMessage("");
		}
	};

	const fileRemoveHandler = (fileUrl: string) => {
		if (imageFiles.find((f) => f.url === fileUrl)) {
			setImageFiles((prev) => prev.filter((fi) => fi.url !== fileUrl));
		} else {
			setDocumentFiles((prev) => prev.filter((df) => df.url !== fileUrl));
		}
	};

	const { text, backgroundColor, color, iconType } = getJobStatusTagDetails(job);

	return (
		<LoggedInLayout width="l">
			<VStack align={"flex-start"} w="full" pb={10}>
				{job ? (
					<>
						<PageTitle>
							#{job.orderNumber}: {job.customer.name}, {getServiceName(job.competence, organisation?.competences)}
						</PageTitle>
						<Back onClick={handleBackNavigation} />
						<HStack width={"100%"} gap={10} alignItems={"start"}>
							{/* Left container */}
							<VStack gap={5} width={"75%"} alignItems={"start"} justifyContent={"start"}>
								<VStack alignItems={"start"} gap={"1px"}>
									<Text fontSize={"14px"} fontWeight={500} color={"#999999"} my={2}>
										Bokade konsulter
									</Text>
									{acceptedConsultants.length > 0 ? (
										<HStack gap={4} my={5} width={"100%"}>
											{acceptedConsultants.map((con) => (
												<ConsultantAvatar
													key={con.imageUrl}
													status={con.status}
													name={con.name}
													imageUrl={con.imageUrl}
													consultantId={con.consultantId}
													jobId={job.id}
													customerId={job.customer.id}
													hasRated={con.hasRated}
													organizationId={organisation?.id}
													emailFeedback={consultantFeedback}
												/>
											))}
										</HStack>
									) : (
										<Text fontSize={"16px"} fontWeight={400} my={2} as={"i"}>
											Ej tillsatt
										</Text>
									)}
								</VStack>

								<VStack alignItems={"start"}>
									<Text fontSize={"14px"} fontWeight={500} color={"#999999"} my={2}>
										Kompetenskrav
									</Text>
									{isEditable ? (
										<VStack alignItems={"start"} gap={3}>
											<HStack>
												<SimpleSelect
													label="Bransch"
													value={selectedBranch}
													onChange={(e) => setSelectedBranch(e.target.value)}
													options={
														competences?.map((item) => ({
															key: item.key,
															value: item.name.sv as string,
														})) ?? []
													}
												/>
												<SimpleSelect
													label="Tjänst"
													value={selectedService}
													onChange={(e) => setSelectedService(e.target.value)}
													options={
														services?.map((item) => ({
															key: item.key,
															value: item.name.sv as string,
														})) ?? []
													}
												/>
											</HStack>
											{showSkills && (
												<TagList
													tagList={skills}
													disabled={skills.length === 0}
													label="Kompetenskrav"
													userGuideText="Välj minst en eller max fem egenskaper inom vald bransch"
													onClick={(item) => handleSkillSelect(item)}
												/>
											)}
										</VStack>
									) : (
										<CompetencePills competences={job.competence} />
									)}
								</VStack>

								<VStack alignItems={"start"} gap={"1px"} width={"100%"}>
									<Text fontSize={"14px"} fontWeight={500} color={"#999999"} my={2}>
										Arbetspass ({job?.shifts?.length || 0})
									</Text>

									<Box minW="100%" my={5}>
										<VStack spacing={4} divider={<StackDivider borderColor="gray.200" />}>
											{shifts.map((s, i) => {
												if (i > showMaxShifts && !showAllShifts) {
													return null;
												}
												return (
													<ShiftRow
														key={s.id || s.start}
														shift={s}
														editable={
															!getConsultantForShift(s.id) &&
															["BOOKED_WITH_UNMANNED_SHIFTS", "OPEN"].includes(job.status)
														}
														onChange={(shift) => updateShift(shift)}
														onRemove={() => removeShift(s.id, s.start)}
														bookedConsultantName={getConsultantForShift(s.id)}
														isLoadingDeleteShift={isLoadingDeleteShift || isLoading}
														avatar={getAvatarForShift(s.id)}
														consultantAbsent={isConsultantAbsentForShift(s)}
													/>
												);
											})}
										</VStack>
										{!showAllShifts && shifts.length > showMaxShifts && (
											<EditLink onClick={() => setShowAllShifts(true)}>{`Visa alla ${
												(job.shifts || []).length
											} pass`}</EditLink>
										)}
									</Box>
									{isShiftsAndCompetenceEditable && <EditLink onClick={appendShift}>+ Lägg till pass</EditLink>}
									<CalendarWrapper>
										<Calendar className="cal" compact shifts={job.shifts} />
									</CalendarWrapper>
								</VStack>

								<VStack alignItems={"start"} gap={2} w={"100%"}>
									<HStack justifyContent="space-between" align={"baseline"} w={"100%"}>
										<Text fontSize={"14px"} fontWeight={500} color={"#999999"} my={2}>
											Arbetsbeskrivning
										</Text>
										<input
											style={{ display: "none" }}
											type="file"
											multiple
											onChange={handleFileUpload}
											ref={fileUploadInputRef}
											accept={"image/jpeg,image/png,application/pdf"}
											disabled={job?.status === "BOOKED"}
										/>
										<Button
											fontSize="14px"
											fontWeight={500}
											color={"#219CD0"}
											variant="link"
											onClick={() => fileUploadInputRef.current?.click()}
											isLoading={isLoadingFileUpload}
											spinner={<LoadingIndicator size={"s"} />}
											isDisabled={job?.status === "BOOKED"}
										>
											+ Ladda upp
										</Button>
									</HStack>
									{isEditable ? (
										<Textarea
											w={"100%"}
											value={jobDescription}
											onChange={(e) => {
												setJobDescription(e.target.value);
											}}
											placeholder="Eventuell mer information om uppdraget, t.ex. vad som ska göras, vem man vikarierar för och annan praktisk information."
										/>
									) : (
										<Text fontSize={"16px"} fontWeight={400} my={2}>
											{job.description ? job.description : "-"}
										</Text>
									)}
									{(![...documentFiles, ...imageFiles] || [...documentFiles, ...imageFiles].length === 0) &&
										!isLoadingFileUpload &&
										!fileErrorMessage && (
											<Text fontSize="16px" fontWeight={400} m={0} color={"#999999"}>
												Inga dokument uppladdade
											</Text>
										)}
									{isLoadingFileUpload && <LoadingIndicator size={"s"} />}
									<Wrap align="center" mr={5}>
										{[...documentFiles, ...imageFiles].map((file, index) => (
											<WrapItem key={index}>
												<FileThumbnail
													onRemove={isEditable ? fileRemoveHandler : undefined}
													fileName={file.name}
													fileUrl={file.url}
												/>
											</WrapItem>
										))}
									</Wrap>
									{fileErrorMessage && (
										<Alert status="error">
											<AlertIcon />
											{fileErrorMessage}
										</Alert>
									)}
								</VStack>

								<VStack alignItems={"start"} gap={"1px"} w={"100%"}>
									<Text fontSize={"14px"} fontWeight={500} color={"#999999"} my={2}>
										Om arbetsplatsen
									</Text>
									{isEditable ? (
										<Textarea
											w={"100%"}
											value={jobCustomerDescription}
											onChange={(e) => {
												setJobCustomerDescription(e.target.value);
											}}
										/>
									) : (
										<Text fontSize={"16px"} fontWeight={400} my={2}>
											{job.customerDescription ? job.customerDescription : "-"}
										</Text>
									)}
								</VStack>

								{job?.metadata.created && (
									<Text color="muted.600" fontSize="sm" fontStyle={"italic"}>
										Uppdraget skapades {formatDateTime(job.metadata.created)} av{" "}
										{job.metadata.createdBy?.firstName || "n/a"}{" "}
										{(job.metadata.createdBy?.lastName || " ").substring(0, 1).toUpperCase()}
									</Text>
								)}
							</VStack>
							{/* Right container */}
							<VStack spacing={"1rem"} width={"25%"}>
								<VStack spacing={"1rem"} align="flex-start">
									<HStack width={"100%"} justify={"space-between"}>
										<Tag size={"sm"} variant="subtle" bg={backgroundColor} borderRadius={"2px"}>
											{iconType && <TagLeftIcon boxSize="12px" as={getIcon(iconType)} />}
											<TagLabel color={color}>{text}</TagLabel>
										</Tag>
									</HStack>
									{getStatusDescription(job) && (
										<Text m={0} fontSize={"14px"} fontWeight={400} color={"#999999"}>
											{getStatusDescription(job)}
										</Text>
									)}
								</VStack>

								<VStack
									spacing={"1rem"}
									flex={2}
									align="flex-start"
									bg={"#F9FAFA"}
									width={"100%"}
									p={5}
									borderRadius={"md"}
									border={`1px solid ${appTheme.colors.dividerLight}`}
								>
									<Link
										to={`${routes.reportsTime}?id=${jobId}`}
										style={{ width: "100%", pointerEvents: job.status !== "BOOKED" ? "none" : "auto" }}
									>
										<Button
											isDisabled={job.status !== "BOOKED"}
											color={appTheme.colors.brand}
											bgColor={"white"}
											w="100%"
											border={`1px solid ${appTheme.colors.dividerLight}`}
											borderRadius="25px"
											fontSize={appTheme.font.small}
											_disabled={{
												color: "gray.400",
												cursor: "not-allowed",
												pointerEvents: "none",
											}}
										>
											Tidrapport
										</Button>
									</Link>

									<Link
										to={`${routes.consultantReports}?id=${jobId}`}
										style={{ width: "100%", pointerEvents: job.status !== "BOOKED" ? "none" : "auto" }}
									>
										<Button
											isDisabled={job.status !== "BOOKED"}
											color={appTheme.colors.brand}
											bgColor={"white"}
											w="100%"
											border={`1px solid ${appTheme.colors.dividerLight}`}
											borderRadius="25px"
											fontSize={appTheme.font.small}
											_disabled={{
												color: "gray.400",
												cursor: "not-allowed",
												pointerEvents: "none",
											}}
										>
											Konsultrapport
										</Button>
									</Link>

									{job.status === "OPEN" && (
										<Button
											color={"#EB5757"}
											bgColor={"white"}
											w="100%"
											border={`1px solid ${appTheme.colors.dividerLight}`}
											borderRadius="25px"
											fontSize={appTheme.font.small}
											onClick={onOpenCancelModal}
										>
											Återkalla uppdrag
										</Button>
									)}

									{job.status === "BOOKED" && (
										<Link
											to={`/uppdrag/${jobId}/tid?services=${getServiceName(job.competence, organisation?.competences)}`}
											style={{ width: "100%" }}
										>
											<Button
												color={appTheme.colors.brand}
												bgColor={"white"}
												w="100%"
												border={`1px solid ${appTheme.colors.dividerLight}`}
												borderRadius="25px"
												fontSize={appTheme.font.small}
											>
												Förläng uppdrag
											</Button>
										</Link>
									)}
								</VStack>

								{invitedConsultants.length > 0 && (
									<>
										{
											<Text textAlign={"center"} my={2} fontSize={"14px"} fontWeight={500} color={"#999999"}>
												{"Önskade konsulter"}
											</Text>
										}
										<Wrap justify="center" spacingY="45px">
											{invitedConsultants.map((con) => (
												<WrapItem key={con.imageUrl}>
													<ConsultantAvatar
														status={con.status}
														name={con.name.split(" ")[0]}
														imageUrl={con.imageUrl}
														boxSize={"65px"}
														consultantId={con.consultantId}
														jobId={job.id}
														customerId={job.customer.id}
														organizationId={organisation?.id}
													/>
												</WrapItem>
											))}
										</Wrap>
									</>
								)}
								{appliedConsultants.length > 0 && (
									<>
										{
											<Text textAlign={"center"} my={2} fontSize={"14px"} fontWeight={500} color={"#999999"}>
												{"Ansökningar"}
											</Text>
										}
										<Wrap justify="center" spacingY="45px">
											{appliedConsultants.map((con) => (
												<WrapItem
													key={con.imageUrl}
													cursor={"pointer"}
													onClick={() => {
														if (con.status === "PENDING_ANSWER") {
															onOpenApplicationAcceptModal();
															setSelectedConsultantApplicationId(con.id);
														}
													}}
												>
													<ConsultantAvatar
														status={con.status}
														name={con.name.split(" ")[0]}
														imageUrl={con.imageUrl}
														boxSize={"65px"}
														consultantId={con.consultantId}
														jobId={job.id}
														customerId={job.customer.id}
														organizationId={organisation?.id}
													/>
												</WrapItem>
											))}
										</Wrap>
									</>
								)}
							</VStack>
						</HStack>
					</>
				) : isLoading ? (
					<LoadingIndicator />
				) : (
					isError && (
						<Alert status="error">
							<AlertIcon />
							Kunde ej läsa in uppdraget, detta kan beror på att uppdraget har tagits bort eller ett tekniskt fel.
						</Alert>
					)
				)}
				{hasChangedOverallData() && (isEditable || job?.status === "BOOKED_WITH_UNMANNED_SHIFTS") && (
					<>
						<Divider />
						<Button
							isLoading={isLoadingJobUpdate}
							isDisabled={isLoadingFileUpload}
							variant="solid"
							height="40px"
							borderRadius={"20px"}
							width={"230px"}
							color="white"
							bg={"#2A2A2A"}
							mx={"auto"}
							fontSize={"16px"}
							fontWeight={500}
							_hover={{
								opacity: 0.7,
							}}
							onClick={handleUpdateJob}
						>
							Spara ändringar
						</Button>
					</>
				)}
			</VStack>

			<Modal isOpen={isOpen} onClose={onClose} size={"lg"}>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader textAlign={"center"}>Osparade ändringar</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Text textAlign={"center"}>{"Det finns osparade ändringar på uppdraget. Vill du gå vidare ändå?"}</Text>
					</ModalBody>

					<ModalFooter>
						<VStack width={"100%"}>
							<Button
								borderRadius={"2px"}
								_hover={{ background: "#4e7da7bd" }}
								bg="#4e7ea7"
								color={"#FFFFFF"}
								width={"100%"}
								mx={3}
								onClick={() => {
									navigate(-1);
								}}
								isLoading={isLoadingDeleteShift}
							>
								Ja, gå vidare
							</Button>
							<Button
								isLoading={isLoadingDeleteShift}
								borderRadius={"2px"}
								onClick={onClose}
								width={"100%"}
								mx={3}
								variant="ghost"
							>
								Nej
							</Button>
						</VStack>
					</ModalFooter>
				</ModalContent>
			</Modal>

			{/* Job Cancel confirm modal  */}

			<Modal isOpen={isOpenCancelModal} onClose={onCloseCancelModal}>
				<ModalOverlay />
				<ModalContent p={4}>
					<ModalHeader textAlign={"center"}>Återkalla uppdrag</ModalHeader>
					<ModalCloseButton />
					<ModalBody textAlign={"center"}>
						<Text m={0} fontWeight={400} fontSize={"15px"} color={"#999999"}>
							Är du säker på att du vill återkalla uppdraget?
						</Text>
					</ModalBody>
					<ModalFooter>
						<VStack width={"100%"}>
							<Button
								borderRadius={"2px"}
								_hover={{ background: "#4e7da7bd" }}
								bg="#4e7ea7"
								color={"#FFFFFF"}
								width={"100%"}
								mx={3}
								onClick={handleJobCancel}
								isLoading={isLoadingJobCancel}
							>
								Ja återkalla
							</Button>
							<Button
								isLoading={isLoadingDeleteShift}
								borderRadius={"2px"}
								onClick={onCloseCancelModal}
								width={"100%"}
								mx={3}
								variant="ghost"
							>
								Nej, avbryt
							</Button>
						</VStack>
					</ModalFooter>
				</ModalContent>
			</Modal>

			{/* Job Application update confirm modal  */}

			<Modal isOpen={isOpenApplicationAcceptModal} onClose={onCloseApplicationAcceptModal}>
				<ModalOverlay />
				<ModalContent p={4}>
					<ModalHeader textAlign={"center"}>Acceptera / Avböja konsult</ModalHeader>
					<ModalCloseButton />
					<ModalBody textAlign={"center"}>
						<Text m={0} fontWeight={400} fontSize={"15px"} color={"#999999"}>
							Är du säker på att acceptera/avböja konsulten för uppdraget?
						</Text>
					</ModalBody>
					<ModalFooter>
						<VStack width={"100%"}>
							<Button
								borderRadius={"2px"}
								_hover={{ background: "#4e7da7bd" }}
								bg="#4e7ea7"
								color={"#FFFFFF"}
								width={"100%"}
								mx={3}
								onClick={() => handleApplicationAccept("ACCEPTED")}
								isLoading={isLoadingJobApplicationUpdate}
							>
								Acceptera
							</Button>
							<Button
								borderRadius={"2px"}
								colorScheme="pink"
								width={"100%"}
								mx={3}
								onClick={() => handleApplicationAccept("DECLINED")}
								isLoading={isLoadingJobApplicationUpdate}
							>
								Avböja
							</Button>
							<Button
								isLoading={isLoadingJobApplicationUpdate}
								borderRadius={"2px"}
								onClick={onCloseApplicationAcceptModal}
								width={"100%"}
								mx={3}
								variant="ghost"
								colorScheme="gray"
							>
								Nej, avbryt
							</Button>
						</VStack>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</LoggedInLayout>
	);
};

export default JobDetailPage;

function sortApplicationsByStatus(a: JobApplication, b: JobApplication) {
	if (a.status === "PENDING_ANSWER" && b.status !== "PENDING_ANSWER") {
		return -1;
	}
	if (b.status === "PENDING_ANSWER" && a.status !== "PENDING_ANSWER") {
		return 1;
	}
	return 0;
}

const CalendarWrapper = styled.div`
	width: 100%;
	margin-top: 1rem;
	border: 1px solid #dedede;
	padding: 10px;
	border-radius: 10px;
`;

const getIcon = (icon: string): React.FC => {
	if (icon === "LOCK") {
		const LockIcon: React.FC = () => (
			<div style={{ marginRight: "5px" }}>
				<Lock size={12} />
			</div>
		);
		LockIcon.displayName = "LockIcon";
		return LockIcon;
	}
	const EmptyIcon: React.FC = () => <span></span>;
	EmptyIcon.displayName = "EmptyIcon";
	return EmptyIcon;
};
